import React, { useEffect } from "react";
import Home from "./pages/Home";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import "./styles/Global.css";
import NotesPage from "./notes/NotesPage";
import { NotesProvider } from "./notes/NotesContext";
import Chat from "./pages/Chat";
import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import ForgotPassword from "./pages/ForgotPassword";
import Pricing from "./pages/Pricing";
import PaymentConfirmation from "./pages/PaymentConfirmation";
import PaymentCallback from "./pages/PaymentCallback";
import Notification from "./components/Notification";
import AnnouncementBanner from "./components/AnnouncementBanner";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Terms from "./pages/Terms";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import QBank from "./pages/QBank";
import QBankCollection from "./pages/QBankCollection";
import BrandDnaV2 from "./pages/BrandDnaV2";
import MockExams from "./pages/MockExams";
import MockExam from "./pages/MockExam";
import MockExamResults from "./pages/MockExamResults";
import MockExamDemo from "./pages/MockExamDemo";
import Admin from "./pages/Admin";
import AdminQBankCollection from "./pages/AdminQBankCollection";
import AdminInstagramCalendar from "./pages/AdminInstagramCalendar";
import AdminTelegramCalendar from "./pages/AdminTelegramCalendar";
import Supervisor from "./pages/Supervisor";
import AcceptInvitation from "./pages/AcceptInvitation";
import ContactUs from "./pages/ContactUs";
import { handleLandingParams } from "./utils/affiliateStorage";
import { initializeGoogleAds } from "./utils/googleAds";
import { useAuthContext } from "./contexts/AuthContext";
import { isQBankCode } from "./utils/qbankProfile";
import TikTokMeasurement from "./components/TikTokMeasurement";
import { captureTikTokLanding } from "./utils/tiktok";

const LegacyQBankRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={`/qbank/sdle${location.search}${location.hash}`} replace />;
};

const showInternalDesignRoutes = process.env.NODE_ENV !== "production";

const QBankBankOrLegacyCollection: React.FC = () => {
  const { bankOrCollectionId } = useParams<{ bankOrCollectionId: string }>();
  return isQBankCode(bankOrCollectionId)
    ? <QBank />
    : <Navigate to={`/qbank/sdle/collections/${bankOrCollectionId}`} replace />;
};

const LegacyMockRedirect: React.FC<{ results?: boolean }> = ({ results = false }) => {
  const { examId } = useParams<{ examId: string }>();
  return <Navigate to={`/qbank/sdle/mock-exams/${examId || ""}${results ? "/results" : ""}`} replace />;
};

/**
 * Component to handle affiliate tracking on route changes
 */
const AffiliateTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    captureTikTokLanding(location.search);
    initializeGoogleAds();
    // Handle affiliate and coupon parameters on route changes
    handleLandingParams();
  }, [location]);

  return null;
};

/**
 * Inner App component that has access to Router context
 */
const AppContent: React.FC = () => {
  const { user } = useAuthContext();
  const location = useLocation();

  // The chat route handles its own banner positioning.
  const showGlobalBanner = !location.pathname.startsWith("/chat");
  const announcementLocation = (() => {
    if (location.pathname === "/") return "home";
    if (location.pathname.startsWith("/pricing")) return "pricing";
    if (
      location.pathname.startsWith("/qbank") ||
      location.pathname.startsWith("/mock-exams")
    ) {
      return "qbank";
    }
    return "global";
  })();

  return (
    <>
      <AffiliateTracker />
      <TikTokMeasurement />
      <Notification />
      {showGlobalBanner && (
        <AnnouncementBanner
          location={announcementLocation}
          userId={user?.id || undefined}
        />
      )}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/notes" element={<NotesPage />} />
        <Route path="/notes/:noteId" element={<NotesPage />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/chat/thread/:thread_id" element={<Chat />} />
        <Route path="/chat/question/:question_id" element={<Chat />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/payment-confirmation" element={<PaymentConfirmation />} />
        <Route path="/payment-callback" element={<PaymentCallback />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/ar/qbank/smle" element={<QBank bankCode="smle" language="ar" />} />
        <Route path="/qbank" element={<LegacyQBankRedirect />} />
        <Route path="/qbank/:bankOrCollectionId" element={<QBankBankOrLegacyCollection />} />
        <Route path="/qbank/:bank/collections/:collectionId" element={<QBankCollection />} />
        <Route path="/qbank/:bank/mock-exams" element={<MockExams />} />
        <Route path="/qbank/:bank/mock-exams/:examId" element={<MockExam />} />
        <Route path="/qbank/:bank/mock-exams/:examId/results" element={<MockExamResults />} />
        {showInternalDesignRoutes && (
          <Route path="/brand-dna-v2" element={<BrandDnaV2 />} />
        )}
        <Route path="/mock-exams" element={<Navigate to="/qbank/sdle/mock-exams" replace />} />
        <Route path="/mock-exams/demo" element={<MockExamDemo />} />
        <Route path="/mock-exams/:examId" element={<LegacyMockRedirect />} />
        <Route path="/mock-exams/:examId/results" element={<LegacyMockRedirect results />} />
        <Route path="/admin" element={<Admin />}>
          <Route path="qbank/collection/:collectionId" element={<AdminQBankCollection />} />
          <Route path="instagram/calendar" element={<AdminInstagramCalendar />} />
          <Route path="telegram/calendar" element={<AdminTelegramCalendar />} />
          <Route path="*" element={null} />
        </Route>
        <Route path="/supervisor" element={<Supervisor />} />
        <Route path="/supervisor/invitations" element={<Supervisor />} />
        <Route path="/supervisor/student/:studentId" element={<Supervisor />} />
        <Route path="/invite/:token" element={<AcceptInvitation />} />
      </Routes>
    </>
  );
};

/**
 * Main App component that defines the routing for the application
 * @returns {JSX.Element} - Rendered app with routing
 */
function App() {
  return (
    <Router>
      <NotesProvider><AppContent /></NotesProvider>
    </Router>
  );
}

export default App;
